import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { reminderKeys } from '@/lib/queryKeys';
import { remindersV2 } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api';
import type { Reminder, ReminderReasonDto } from '@/types/reminder';

type ActionResponse = { data: Reminder | { stepId: string; enqueuedJobId: string | null } };

interface ReasonVariables {
  id: string;
  reason?: string;
}

/**
 * Reminder lifecycle actions — pause/resume/cancel/skip-next/send-now
 * (spec §13.3). Returns a single hook with all mutations so callers wire one
 * action menu against a unified loading surface.
 *
 * Every mutation invalidates both the active list (queue) and the detail
 * drawer query for the affected reminder.
 */
export function useReminderActions(options?: { sendNowSuccessMessage?: string }) {
  const queryClient = useQueryClient();

  function invalidateAll(id?: string) {
    void queryClient.invalidateQueries({ queryKey: reminderKeys.lists() });
    if (id) {
      void queryClient.invalidateQueries({ queryKey: reminderKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: reminderKeys.previewNextStep(id) });
      void queryClient.invalidateQueries({ queryKey: reminderKeys.auditLog(id) });
    }
  }

  const pause = useApiMutation<ActionResponse, ReasonVariables>({
    mutationFn: ({ id, reason }) =>
      apiPost<ActionResponse, ReminderReasonDto>(operationsClient, remindersV2.pause(id), {
        reason,
      }),
    onSuccess: (_data, variables) => {
      invalidateAll(variables.id);
      toast.success('Reminder paused');
    },
  });

  const resume = useApiMutation<ActionResponse, { id: string }>({
    mutationFn: ({ id }) => apiPost<ActionResponse>(operationsClient, remindersV2.resume(id)),
    onSuccess: (_data, variables) => {
      invalidateAll(variables.id);
      toast.success('Reminder resumed');
    },
  });

  const cancel = useApiMutation<ActionResponse, ReasonVariables>({
    mutationFn: ({ id, reason }) =>
      apiPost<ActionResponse, ReminderReasonDto>(operationsClient, remindersV2.cancel(id), {
        reason,
      }),
    onSuccess: (_data, variables) => {
      invalidateAll(variables.id);
      toast.success('Reminder cancelled');
    },
  });

  const skip = useApiMutation<ActionResponse, ReasonVariables>({
    mutationFn: ({ id, reason }) =>
      apiPost<ActionResponse, ReminderReasonDto>(operationsClient, remindersV2.skipNextStep(id), {
        reason,
      }),
    onSuccess: (_data, variables) => {
      invalidateAll(variables.id);
      toast.success('Step skipped');
    },
  });

  const sendNow = useApiMutation<ActionResponse, { id: string }>({
    mutationFn: ({ id }) => apiPost<ActionResponse>(operationsClient, remindersV2.sendNow(id)),
    onSuccess: (_data, variables) => {
      invalidateAll(variables.id);
      toast.success(options?.sendNowSuccessMessage ?? 'Step queued for immediate send');
    },
  });

  return { pause, resume, cancel, skip, sendNow };
}
